import React, { useState, useEffect } from 'react';
import {
  Accordion,
  Button,
  Group,
  NavLink,
  Text,
  MantineProvider,
  createTheme,
  Tabs,
  Card,
  Space,
  Container,
  Drawer,
  ActionIcon,
  Burger,
  Box,
} from '@mantine/core';
import { useMediaQuery, useDisclosure } from '@mantine/hooks';
import { Link } from '@inertiajs/react';
import {
  IconPlus,
  IconSearch,
  IconMapPin,
  IconWorld,
  IconMenu2,
  IconUserFilled,
  IconSettings,
  IconNotebook,
  IconWallet,
  IconSchool,
  IconBriefcase,
  IconBookmarks,
  IconCalendarStats,
  IconList,
  IconUsers,
  IconReceiptDollar,
  IconReceipt,
  IconLogin2,
  IconUserPlus,
} from '@tabler/icons-react';
import PrivateLessonsManagement from '../../components/PrivateLessons/PrivateLessonsManagement';
import UserAccount from '../../components/UserAccount';
import BookmarkedEvents from '../../pages/Users/BookmarkedEvents';
import ManagedEvents from '../../pages/Users/ManagedEvents';
import ManagedOrganizations from '../../pages/Users/ManagedOrganizations';
import ManagedArtists from '../../pages/Users/ManagedArtists';
import EditUser from '../../pages/Users/EditUser';
import { showNotification, Notifications } from '@mantine/notifications';

import '@mantine/core/styles.css';
import '@mantine/notifications/styles.css';
import '../../styles/UserSettings.css';

// Define your custom theme
const theme = createTheme({
  primaryColor: 'orange',
  colors: {
    orange: [
      '#FEF5ED',
      '#FDE3D1',
      '#FCB693',
      '#FA9D73',
      '#F87F4D',
      '#F66A33',
      '#EA580C',
      '#C44B0A',
      '#9E3D08',
      '#783006',
    ],
  },
  fontFamily: 'Poppins, sans-serif',
  headings: {
    fontFamily: 'Poppins, sans-serif',
  },
  globalStyles: (theme) => ({
    body: { color: '#f2f2f2' },
    '.sidebar-content': { color: '#f2f2f2' },
  }),
});

interface UserSettingsProps {
  user: {
    id: number;
    username: string;
    bio: string;
    email: string;
    name: string;
    superadmin?: boolean;
    hasOrganizer?: boolean;
    hasArtist?: boolean;
    artists?: Array<{ id: number; name: string; isPortalEnabled: boolean }>;
    claimed_artist?: {
      id: number;
      name: string;
      slug: string;
      private_lessons_enabled: boolean;
    };
  };
  initial_section: string;
  available_roles?: Array<{
    id: number;
    name: string;
  }>;
  pendingCounter?: number; // Explicitly typed as number
  deletedCounter?: number;
  subscriptions_by_type?: any;
  private_lesson_types?: Array<{
    id: number;
    name: string;
    location: string;
    duration: number;
    price: number;
    payment_required: boolean;
    color: string;
  }>;
  private_lesson_pages?: Array<{
    id: number;
    description: string;
    additional_info: string;
    timezone: string;
    slug: string;
    created_at: string;
    private_lesson_type: {
      id: number;
      name: string;
      location: string;
      duration: number;
      price: number;
      color: string;
    };
  }>;
  private_lesson_bookings?: Array<{
    id: number;
    user_id: number;
    private_lesson_page_id: number;
    start_time: string;
    end_time: string;
    status: string;
  }>;
  managed_events?: {
    approved_events?: Array<any>;
    pending_events?: Array<any>;
    recurring_events?: Array<any>;
    draft_events?: Array<any>;
    approved_pagination?: {
      current_page: number;
      per_page: number;
      total_pages: number;
    };
    pending_pagination?: {
      current_page: number;
      per_page: number;
      total_pages: number;
    };
    recurring_pagination?: {
      current_page: number;
      per_page: number;
      total_pages: number;
    };
    draft_pagination?: {
      current_page: number;
      per_page: number;
      total_pages: number;
    };
  };
  metadata?: {
    approved_count: number;
    pending_count: number;
    recurring_count: number;
    draft_count: number;
  };
}

/* RightContentSection Component */
interface RightContentSectionProps {
  section: string;
  userData?: UserSettingsProps['user'];
  setUserData?: React.Dispatch<React.SetStateAction<UserSettingsProps['user']>>;
  user: UserSettingsProps['user'];
  claimedArtist?: UserSettingsProps['user']['claimed_artist'];
  privateLessonTypes?: Array<{
    id: number;
    name: string;
    location: string;
    duration: number;
    price: number;
    payment_required: boolean;
    color: string;
  }>;
  privateLessonPages?: Array<{
    id: number;
    description: string;
    additional_info: string;
    timezone: string;
    slug: string;
    created_at: string;
    private_lesson_type: {
      id: number;
      name: string;
      location: string;
      duration: number;
      price: number;
      color: string;
    };
  }>;
  privateLessonBookings?: Array<{
    id: number;
    user_id: number;
    private_lesson_page_id: number;
    start_time: string;
    end_time: string;
    status: string;
  }>;
  initialTab?: string | null;
  available_roles?: Array<{ value: string; label: string }>;
  approved_events?: Array<any>;
  pending_events?: Array<any>;
  recurring_events?: Array<any>;
  draft_events?: Array<any>;
  managed_events?: {
    approved_pagination?: {
      current_page: number;
      per_page: number;
      total_pages: number;
    };
    pending_pagination?: {
      current_page: number;
      per_page: number;
      total_pages: number;
    };
    recurring_pagination?: {
      current_page: number;
      per_page: number;
      total_pages: number;
    };
    draft_pagination?: {
      current_page: number;
      per_page: number;
      total_pages: number;
    };
  };
  active_tab?: string;
  metadata?: {
    approved_count: number;
    pending_count: number;
    recurring_count: number;
    draft_count: number;
  };
  openEditDrawer?: () => void;
  opened?: boolean;
  close?: () => void;
}

const RightContentSection: React.FC<RightContentSectionProps> = ({
  section,
  user,
  claimedArtist,
  privateLessonTypes,
  privateLessonPages,
  privateLessonBookings,
  initialTab,
  available_roles,
  managed_events,
  metadata,
  active_tab,
  approved_events,
  pending_events,
  recurring_events,
  draft_events,
  openEditDrawer,
  opened,
  close,
  userData,
  setUserData,
}) => {
  // Add detailed logging
  // console.log('RightContentSection props:', {
  //   section,
  //   claimedArtist,
  //   privateLessonTypes: privateLessonTypes?.length,
  //   privateLessonPages: privateLessonPages?.length,
  //   privateLessonBookings: privateLessonBookings?.length,
  //   initialTab,
  // });
  // // console.log('privateLessonPages detail:', privateLessonPages);
  // console.log('🎯🎯🎯🎯🎯 UserSettings Props managed_events:', managed_events);
  // console.log('👽👽👽👽👽 UserSettings direct props:', {
  //   approved_events,
  //   pending_events,
  //   recurring_events,
  //   draft_events,
  // });

  if (section === 'settings') {
    function setActiveSection(section: string) {
      localStorage.setItem('activeSettingsSection', section);
      window.dispatchEvent(new CustomEvent('navigateToPrivateLessonBookings', { detail: { section } }));
    }
    return (
      <Container style={{ padding: '1rem' }}>
        <Notifications position="top-right" />
        <Text size="xl" fw={700} mb="1rem" className="us-settings-header">
          Your Settings
        </Text>
        {/* Tab Navigation */}
        <Tabs defaultValue="profile">
          <Tabs.List>
            <Tabs.Tab value="profile">Profile</Tabs.Tab>
            <Tabs.Tab value="payments" disabled>
              Payments
            </Tabs.Tab>
          </Tabs.List>

          <Tabs.Panel value="profile" pt="xs">
            {/* Profile Tab Content */}
            <Card
              shadow="sm"
              padding="lg"
              radius={8}
              style={{
                minHeight: '100px',
                marginBottom: '1rem',
                backgroundColor: '#757575',
                color: '#f2f2f2',
              }}
            >
              <Text fw={700} mb="xs" c="#f2f2f2" className="us-settings-header">
                Your User Account
              </Text>
              <Text mb="md" c="#f2f2f2" className="us-settings-header mb-3">
                Update your email address, password, and other personal information.
              </Text>
              <Button onClick={openEditDrawer} variant="filled">
                Edit Your User Account
              </Button>
            </Card>
            <Card
              shadow="sm"
              padding="lg"
              radius={8}
              style={{ minHeight: '100px', backgroundColor: '#757575', color: '#f2f2f2' }}
            >
              <Text fw={500} mb="xs" className="us-settings-header">
                Your Subscriptions
              </Text>
              <Text mb="md" className="us-settings-header">
                Manage your subscriptions here. You will get an email weekly every Sunday morning with upcoming events.
              </Text>
              <Button
                component="a"
                href={`/user_subscriptions/${user.id}`}
                variant="filled"
                disabled
                className="clear-button mt-3"
              >
                Manage Your Subscriptions
              </Button>
            </Card>
          </Tabs.Panel>

          <Tabs.Panel value="payments" pt="xs">
            {/* Payments Tab Content */}
            <Card
              shadow="sm"
              padding="lg"
              radius={8}
              style={{
                minHeight: '100px',
                marginBottom: '1rem',
                backgroundColor: '#757575',
                color: '#f2f2f2',
              }}
            >
              <Text fw={500} mb="xs" className="us-settings-header">
                Payment Methods
              </Text>
              <Text mb="md" className="us-settings-header">
                Monetize your following by connecting to Stripe to receive payments from your supporters.
              </Text>
              <Button component="a" href="/connect_stripe" variant="filled">
                Connect to Stripe
              </Button>
            </Card>
            <Card
              shadow="sm"
              padding="lg"
              radius={8}
              style={{ minHeight: '100px', backgroundColor: '#757575', color: '#f2f2f2' }}
            >
              <Text component="div" fw={500} mb="xs" className="us-settings-header">
                Currency
              </Text>
              <select className="form-select" id="currency" defaultValue="USD">
                <option value="USD">US $</option>
                <option value="CAD">CAD $</option>
                <option value="EUR">EUR €</option>
              </select>
            </Card>
          </Tabs.Panel>
        </Tabs>

        <Drawer
          opened={opened}
          onClose={close}
          title="Edit User Account"
          position="right"
          size="lg"
          padding="xl"
          overlayProps={{ opacity: 0.55, blur: 4 }}
          styles={{
            root: { zIndex: 1000 },
            header: {
              backgroundColor: '#18181B',
              color: '#f2f2f2',
              borderBottom: '1px solid #333',
            },
            body: {
              backgroundColor: '#18181B',
              color: '#f2f2f2',
              minHeight: '100vh',
              paddingTop: '2rem',
            },
            close: {
              color: '#f2f2f2',
              '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.1)' },
            },
          }}
        >
          <EditUser
            user={userData} // Use state variable instead of direct props
            update_url={`/users/${userData.username}`}
            available_roles={
              available_roles?.map((role) => ({
                value: role.value?.toString() || '',
                label: role.label || '',
              })) || []
            }
            all_events={managed_events?.approved_events || []}
            all_artists={userData.artists || []}
            onSuccess={(updatedData) => {
              if (close) {
                close();
              }

              // If we have updated data, update the state
              if (updatedData && setUserData) {
                console.log('Updating userData state with:', updatedData);
                setUserData(updatedData);
              }

              setActiveSection('settings');
            }}
          />
        </Drawer>
      </Container>
    );
  } else if (section === 'privateLessons' && claimedArtist) {
    // console.log('Rendering PrivateLessonsManagement with:', {
    //   privateLessonTypes,
    //   privateLessonPages,
    //   privateLessonBookings,
    //   initialTab,
    // });

    // When privateLessons section is active, render the PrivateLessonsManagement component
    // Pass the initialTab to the component
    return (
      <PrivateLessonsManagement
        currentUser={user}
        claimedArtist={claimedArtist}
        privateLessonTypes={privateLessonTypes}
        privateLessonPages={privateLessonPages}
        privateLessonBookings={privateLessonBookings}
        available_roles={available_roles?.map((role) => ({ value: role.id.toString(), label: role.name }))}
      />
    );
  } else if (section === 'account') {
    return (
      <Container style={{ padding: '1rem' }}>
        <Text size="xl" fw={700} mb="1rem" className="us-settings-header">
          Manage Your Account
        </Text>
        <UserAccount
          user={{
            ...user,
            role_ids: [],
            event_ids: [],
            artist_ids: [],
            organizer_ids: [],
            private_lesson_feature_flag: false,
            avatar_url: null,
            post_seo_image_url: null,
          }}
          update_url={`/update_profile/${user.username}`}
          available_roles={available_roles}
          onUpdateSuccess={() => {
            // Optional callback for when the update succeeds
            // For example, you could show a notification or refresh data
          }}
        />
      </Container>
    );
  } else if (section === 'bookmarkedEvents') {
    return <BookmarkedEvents user={user} />;
  } else if (section === 'managedEvents') {
    // console.log('🔥🔥🔥🔥🔥 managed_events prop:', managed_events);
    // console.log('🌟🌟🌟🌟🌟 approved_events prop:', approved_events);
    // console.log('🚀🚀🚀🚀🚀 pending_events prop:', pending_events);
    // console.log('💫💫💫💫💫 recurring_events prop:', recurring_events);
    // console.log('🌈🌈🌈🌈🌈 draft_events prop:', draft_events);
    // console.log('📊📊📊📊📊 metadata:', metadata);

    return (
      <ManagedEvents
        user={user}
        approved_events={approved_events || []}
        pending_events={pending_events || []}
        recurring_events={recurring_events || []}
        draft_events={draft_events || []}
        approved_pagination={managed_events?.approved_pagination}
        pending_pagination={managed_events?.pending_pagination}
        recurring_pagination={managed_events?.recurring_pagination}
        draft_pagination={managed_events?.draft_pagination}
        active_tab={active_tab || 'approved'}
        metadata={metadata}
      />
    );
  } else if (section === 'managedOrganizations') {
    return <ManagedOrganizations organizers={user.organizers || []} />;
  } else if (section === 'managedArtists') {
    return <ManagedArtists artists={user.artists || []} claimedArtist={user.claimedArtist} />;
  }

  // For other sections or default content
  return (
    <Container style={{ padding: '1rem' }}>
      <Text size="xl" fw={700} mb="1rem" className="us-settings-header">
        Welcome to Your Dashboard
      </Text>
      <Card shadow="sm" padding="lg" radius={8} style={{ backgroundColor: '#757575', color: '#f2f2f2' }}>
        <Text className="us-settings-header">Select an option from the sidebar to manage your account.</Text>
      </Card>
    </Container>
  );
};

/* Main UserSettings Page */
const UserSettings: React.FC<UserSettingsProps> = ({
  user,
  pendingCounter = 0,
  deletedCounter = 0,
  subscriptions_by_type,
  private_lesson_types,
  private_lesson_pages,
  private_lesson_bookings,
  available_roles,
  managed_events,
  metadata,
  initial_section,
}) => {
  // Check if we're on mobile
  const isMobile = useMediaQuery('(max-width: 768px)');

  // State for sidebar visibility on mobile
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [opened, { open, close }] = useDisclosure(false);
  const [userData, setUserData] = useState(user);

  // Add console log to debug props
  // console.log('UserSettings Props:', {
  //   user,
  //   pendingCounter,
  //   deletedCounter,
  //   subscriptions_by_type,
  //   privateLessonTypes: private_lesson_types?.length,
  //   privateLessonPages: private_lesson_pages?.length,
  //   privateLessonBookings: private_lesson_bookings?.length,
  // });

  const isSuperadmin = user && user.superadmin;
  const hasOrganizer = user && user.hasOrganizer;
  const hasArtist = user && user.hasArtist;
  const portalArtists = user?.artists?.filter((a) => a.isPortalEnabled) || [];

  console.log('hasOrganizer:', hasOrganizer);
  console.log('hasArtist:', hasArtist);
  console.log('portalArtists:', portalArtists);

  // Parse URL query parameters to get the section and tab
  const [activeSection, setActiveSection] = useState<string>(() => {
    if (initial_section) {
      // console.log('Setting active section from initial_section:', initial_section);
      return initial_section;
    }
    // Check URL parameters first
    const urlParams = new URLSearchParams(window.location.search);
    const sectionParam = urlParams.get('section');

    // Then check localStorage (this might be set by the ManageBadge component)
    const storedSection = localStorage.getItem('activeSettingsSection');

    // Return the section from URL params, localStorage, or default to 'default'
    return sectionParam || storedSection || 'default';
  });

  // Initialize tab state for PrivateLessonsManagement
  const [activeTab, setActiveTab] = useState<string | null>(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const tabParam = urlParams.get('tab');
    const storedTab = localStorage.getItem('activePrivateLessonsTab');
    return tabParam || storedTab || null;
  });

  // Setup event listener for tab navigation
  useEffect(() => {
    const handleNavigation = (event: CustomEvent) => {
      const { section, tab } = event.detail;
      if (section) setActiveSection(section);
      if (tab) setActiveTab(tab);
    };

    // Add event listener
    window.addEventListener('navigateToPrivateLessonBookings', handleNavigation as EventListener);

    // Clean up
    return () => {
      window.removeEventListener('navigateToPrivateLessonBookings', handleNavigation as EventListener);

      // Optionally clear localStorage when component unmounts
      localStorage.removeItem('activeSettingsSection');
      localStorage.removeItem('activePrivateLessonsTab');
    };
  }, []);

  // Update URL with current state of active section and tab
  useEffect(() => {
    const url = new URL(window.location.href);
    if (activeSection !== 'default') {
      url.searchParams.set('section', activeSection);
    } else {
      url.searchParams.delete('section');
    }

    if (activeTab) {
      url.searchParams.set('tab', activeTab);
    } else {
      url.searchParams.delete('tab');
    }

    // Update browser history without reloading the page
    window.history.replaceState({}, '', url.toString());
  }, [activeSection, activeTab]);

  // Function to close sidebar when a menu item is clicked (mobile only)
  const handleMenuItemClick = (section: string) => {
    setActiveSection(section);
    if (isMobile) {
      setSidebarOpen(false);
    }
  };

  // Create the sidebar content (used for both desktop and mobile)
  const SidebarContent: React.FC<{
    user: UserSettingsProps['user'];
    pendingCounter: number;
    deletedCounter: number;
    handleMenuItemClick: (section: string) => void;
  }> = ({ user, pendingCounter, deletedCounter, handleMenuItemClick }) => (
    <ul className="nav flex-column">
      <li className="sidebar-section">
        <Text size="sm" fw={500} mt="md" className="us-settings-header">
          WhereCanWeDance
        </Text>
      </li>

      {user && (
        <>
          <li className="sidebar-divider" />
          <li className="sidebar-section">
            <NavLink
              component="button"
              onClick={() => handleMenuItemClick('account')}
              label="Manage Your Account"
              leftSection={<IconUserFilled size={16} color="white" />}
              disabled
            />
            <NavLink
              component="button"
              leftSection={<IconSettings size={16} color="white" />}
              onClick={() => handleMenuItemClick('settings')}
              label="Settings"
            />
          </li>

          <li className="sidebar-divider" />
          <li className="sidebar-section">
            <Text size="sm" fw={500} className="us-settings-header">
              Dance Life
            </Text>
            <NavLink
              component={Link}
              c="#f2f2f2"
              href={`/dance_journal/${user.id}`}
              leftSection={<IconNotebook size={16} />}
              label="Your Dance Journal"
              disabled
            />
            <NavLink
              component={Link}
              c="#f2f2f2"
              leftSection={<IconWallet size={16} />}
              href="#"
              label="Your Dance Wallet"
              disabled
            />
            <NavLink
              component={Link}
              c="#f2f2f2"
              leftSection={<IconSchool size={16} />}
              href={`/your_private_lessons/${user.id}`}
              label="Your Private Lessons"
              disabled
            />
          </li>

          {hasOrganizer && (
            <>
              <li className="sidebar-divider" />
              <li className="sidebar-section">
                <Text size="sm" fw={500} className="us-settings-header">
                  Organizations
                </Text>
                <NavLink
                  component={Link}
                  c="#f2f2f2"
                  leftSection={<IconBriefcase size={16} />}
                  href={`/users/${user.username}/manage-organizations`}
                  label="Your Organizations"
                />
              </li>
            </>
          )}

          <li className="sidebar-divider" />
          <li className="sidebar-section">
            <Text size="sm" fw={500} className="us-settings-header">
              Your Events
            </Text>
            <NavLink
              component={Link}
              c="#f2f2f2"
              leftSection={<IconBookmarks size={16} />}
              href={`/users/${user.username}/user-settings?section=bookmarkedEvents`}
              label="Your Bookmarked Events"
            />
            <NavLink
              component={Link}
              leftSection={<IconCalendarStats size={16} />}
              c="#f2f2f2"
              href={`/users/${user.username}/managed-events`}
              label="Events You Manage"
            />
          </li>

          <li className="sidebar-divider" />
          <li className="sidebar-section">
            <Text size="sm" fw={500} className="us-settings-header">
              Your Artists
            </Text>
            <NavLink
              component={Link}
              c="#f2f2f2"
              leftSection={<IconList size={16} />}
              href={`/your_booking_portals/${user.id}`}
              label="Your Artist Booking Portal(s)"
              disabled
            />
            <NavLink
              component={Link}
              c="#f2f2f2"
              leftSection={<IconUsers size={16} />}
              href={`/users/${user.username}/managed-artists`}
              label="Artists You Manage"
            />
            <NavLink
              component="button"
              c="#f2f2f2"
              leftSection={<IconReceiptDollar size={16} />}
              onClick={() => {
                handleMenuItemClick('privateLessons');
                // Optionally set active tab here
              }}
              label="Private Lesson Management"
              disabled
            />
          </li>

          <li className="sidebar-divider" />
          <li className="sidebar-section">
            <Text size="sm" fw={500} className="us-settings-header">
              Your Purchases
            </Text>
            <NavLink
              component={Link}
              c="#f2f2f2"
              leftSection={<IconReceipt size={16} />}
              href={`/star_purchases/${user.id}`}
              label="View Star Purchases"
              disabled
            />
          </li>
        </>
      )}

      {!user && (
        <>
          <li className="sidebar-divider" />
          <li className="sidebar-section">
            <NavLink component={Link} leftSection={<IconLogin2 size={16} />} href="/login" label="Log in" />
            <NavLink component={Link} leftSection={<IconUserPlus size={16} />} href="/signup" label="Sign up" />
          </li>
        </>
      )}
    </ul>
  );

  return (
    <MantineProvider theme={theme}>
      {isMobile ? (
        <div style={{ display: 'block', maxWidth: '100%', margin: '0 auto' }}>
          {/* Mobile Toggle Button - Below Navbar */}
          <div
            style={{
              position: 'sticky',
              top: 0,
              left: 0,
              width: '100%',
              backgroundColor: '#EA580C',
              padding: '10px 15px',
              zIndex: 100,
            }}
          >
            <Button
              leftSection={<IconMenu2 size={20} />}
              variant="subtle"
              color="orange"
              fullWidth
              className="zindex-1"
              style={{ color: '#f2f2f2', height: '30px', zIndex: 1 }}
              onClick={() => setSidebarOpen(!sidebarOpen)}
            >
              Dashboard Menu
            </Button>
          </div>

          {/* Main Content for Mobile */}
          <div style={{ width: '100%', paddingTop: '0.5rem' }}>
            <RightContentSection
              section={activeSection}
              user={user}
              userData={userData}
              setUserData={setUserData}
              claimedArtist={user.claimed_artist}
              privateLessonTypes={private_lesson_types}
              privateLessonPages={private_lesson_pages}
              privateLessonBookings={private_lesson_bookings}
              initialTab={activeTab}
              available_roles={available_roles}
              // Extract and pass event arrays from managed_events
              approved_events={managed_events?.approved_events}
              pending_events={managed_events?.pending_events}
              recurring_events={managed_events?.recurring_events}
              draft_events={managed_events?.draft_events}
              // Pass managed_events for pagination
              managed_events={{
                approved_pagination: managed_events?.approved_pagination,
                pending_pagination: managed_events?.pending_pagination,
                recurring_pagination: managed_events?.recurring_pagination,
                draft_pagination: managed_events?.draft_pagination,
              }}
              metadata={metadata}
              active_tab={initial_section === 'managedEvents' ? 'approved' : undefined}
              openEditDrawer={open}
              opened={opened} // Add this
              close={close}
            />
          </div>

          {/* Mobile Sidebar Drawer */}
          <Drawer
            opened={sidebarOpen}
            onClose={() => setSidebarOpen(false)}
            size="lg"
            position="bottom"
            zIndex={1001}
            styles={{
              header: { backgroundColor: '#171717' },
              body: {
                backgroundColor: '#171717',
                padding: '0rem 0 0 0',
                minHeight: '100%',
                height: 'auto',
              },
            }}
          >
            <div className="sidebar-content" style={{ height: '100%', padding: '1rem' }}>
              <SidebarContent
                user={user}
                pendingCounter={pendingCounter}
                deletedCounter={deletedCounter}
                handleMenuItemClick={handleMenuItemClick}
              />
            </div>
          </Drawer>
        </div>
      ) : (
        /* For desktop: Flex layout with sidebar and content side by side */
        <div
          style={{
            display: 'flex',
            position: 'relative',
            width: '100%', // Set explicit width
            maxWidth: '100%',
            margin: '0 auto',
            overflow: 'hidden', // Prevent overall container overflow
          }}
        >
          {/* Desktop Sidebar - Fixed width */}
          <div
            className="sidebar-content"
            style={{
              width: '250px',
              minWidth: '250px', // Ensure sidebar doesn't shrink
              minHeight: '100vh',
              padding: '1rem',
            }}
          >
            <SidebarContent
              user={user}
              pendingCounter={pendingCounter}
              deletedCounter={deletedCounter}
              handleMenuItemClick={handleMenuItemClick}
            />
          </div>

          {/* Main Content for Desktop - Flexible width */}
          <div
            style={{
              flex: '1 1 auto', // Grow and shrink as needed
              width: 'calc(100% - 260px)', // Calculate exact available width
              maxWidth: 'calc(100% - 260px)', // Prevent exceeding available space
              paddingTop: '1.5rem',
              overflowX: 'hidden', // Prevent horizontal scrolling within this container
              boxSizing: 'border-box', // Include padding in width calculation
            }}
          >
            <RightContentSection
              section={activeSection}
              user={user}
              userData={userData}
              setUserData={setUserData}
              claimedArtist={user.claimed_artist}
              privateLessonTypes={private_lesson_types}
              privateLessonPages={private_lesson_pages}
              privateLessonBookings={private_lesson_bookings}
              initialTab={activeTab}
              available_roles={available_roles}
              approved_events={managed_events?.approved_events}
              pending_events={managed_events?.pending_events}
              recurring_events={managed_events?.recurring_events}
              draft_events={managed_events?.draft_events}
              // Pass managed_events for pagination
              managed_events={managed_events}
              metadata={metadata}
              active_tab={initial_section === 'managedEvents' ? 'approved' : undefined}
              openEditDrawer={open}
              opened={opened} // Add this
              close={close}
            />
          </div>
        </div>
      )}
    </MantineProvider>
  );
};

export default UserSettings;
